import { useState, useEffect, useCallback, useRef } from "react";
import {
  userSettingsService,
  HolidaySettings,
  DisplaySettings,
  TimezoneSettings,
  LocaleSettings,
  ProjectSettings,
  Country,
  State,
} from "@/services/userSettings";
import { getWeekStartDay, WeekStartDay } from "@/utils/weekSettings";

export function useUserSettings(enabled: boolean = true) {
  const [holidaySettings, setHolidaySettings] = useState<HolidaySettings>({
    country: "US",
    showHolidays: true,
    holidayColor: "#ef4444",
  });

  const [displaySettings, setDisplaySettings] = useState<DisplaySettings>({
    showTimezoneIndicator: true,
    showOverlapIndicator: true,
    showActiveEntryAnimations: true,
    showBudgetWarnings: true,
    overlapIndicatorColor: "#FEF2F2",
    budgetWarningColor: "#FEF2F2",
    defaultCurrency: 'EUR',
  });

  const [timezoneSettings, setTimezoneSettings] = useState<TimezoneSettings>({
    preferredTimezone: null, // Default to null (use browser timezone)
  });

  const [localeSettings, setLocaleSettings] = useState<LocaleSettings>({
    weekStartsOn: 'auto',
  });

  const [projectSettings, setProjectSettings] = useState<ProjectSettings>({
    autoArchiveMonths: 12,
  });

  const [countries, setCountries] = useState<Country[]>([]);
  const [states, setStates] = useState<State[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Track pending timezone update to prevent duplicate API calls
  // This is needed because CustomDropdown's grouped options can trigger double-clicks
  const pendingTimezoneUpdate = useRef<string | null | undefined>(undefined);

  // Load initial data only when enabled (user is authenticated)
  useEffect(() => {
    if (enabled) {
      loadInitialData();
    }
  }, [enabled]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load user settings and countries in parallel
      const [settingsResponse, countriesResponse] = await Promise.all([
        userSettingsService.getUserSettings(),
        userSettingsService.getCountries(),
      ]);

      setHolidaySettings(settingsResponse.holidaySettings);
      // Merge with defaults to ensure new properties have fallback values
      // even when user has existing displaySettings without them
      const displaySettingsDefaults = { showTimezoneIndicator: true, showOverlapIndicator: true, showActiveEntryAnimations: true, showBudgetWarnings: true, overlapIndicatorColor: "#FEF2F2", budgetWarningColor: "#FEF2F2", defaultCurrency: 'EUR' };
      // Migrate defaultCurrency from legacy currencySettings if not yet stored in displaySettings
      const migratedCurrency = settingsResponse.displaySettings?.defaultCurrency
        ?? settingsResponse.currencySettings?.defaultCurrency
        ?? 'EUR';
      setDisplaySettings({ ...displaySettingsDefaults, ...settingsResponse.displaySettings, defaultCurrency: migratedCurrency });
      // Merge with defaults for timezone settings
      const timezoneSettingsDefaults = { preferredTimezone: null };
      setTimezoneSettings({ ...timezoneSettingsDefaults, ...settingsResponse.timezoneSettings });
      // Merge with defaults for locale settings
      const localeSettingsDefaults = { weekStartsOn: 'auto' as const };
      setLocaleSettings({ ...localeSettingsDefaults, ...settingsResponse.localeSettings });
      // Merge with defaults for project settings
      const projectSettingsDefaults = { autoArchiveMonths: 12 };
      setProjectSettings({ ...projectSettingsDefaults, ...settingsResponse.projectSettings });
      setCountries(countriesResponse.countries);
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Failed to load settings. Please refresh the page.");

      // Set fallback data
      setCountries([
        { code: "US", name: "United States" },
        { code: "CA", name: "Canada" },
        { code: "DE", name: "Germany" },
        { code: "GB", name: "United Kingdom" },
        { code: "FR", name: "France" },
        { code: "IT", name: "Italy" },
        { code: "ES", name: "Spain" },
        { code: "AU", name: "Australia" },
        { code: "JP", name: "Japan" },
        { code: "BR", name: "Brazil" },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadStates = useCallback(
    async (countryCode: string) => {
      try {
        const response = await userSettingsService.getStates(countryCode);
        setStates(response.states);

        // Clear state selection if it's not valid for the new country
        if (
          holidaySettings.state &&
          !response.states.find((s) => s.code === holidaySettings.state)
        ) {
          setHolidaySettings((prev) => ({ ...prev, state: undefined }));
        }
      } catch (err) {
        console.error("Error loading states:", err);
        setStates([]);

        // Set fallback states for common countries
        const fallbackStates: Record<string, State[]> = {
          US: [
            { code: "CA", name: "California" },
            { code: "NY", name: "New York" },
            { code: "TX", name: "Texas" },
            { code: "FL", name: "Florida" },
          ],
          CA: [
            { code: "ON", name: "Ontario" },
            { code: "QC", name: "Quebec" },
            { code: "BC", name: "British Columbia" },
          ],
          DE: [
            { code: "BY", name: "Bavaria" },
            { code: "NW", name: "North Rhine-Westphalia" },
            { code: "BE", name: "Berlin" },
          ],
        };

        setStates(fallbackStates[countryCode] || []);
      }
    },
    [holidaySettings.state]
  );

  // Load states when country changes
  useEffect(() => {
    if (holidaySettings.country) {
      loadStates(holidaySettings.country);
    }
  }, [holidaySettings.country, loadStates]);

  const updateHolidaySettings = async (
    newSettings: Partial<HolidaySettings>
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await userSettingsService.updateHolidaySettings(
        newSettings
      );
      setHolidaySettings(response.holidaySettings);
      setSuccess(response.message);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error updating holiday settings:", err);
      setError("Failed to update holiday settings. Please try again.");
      throw err; // Re-throw to allow callers to handle rollback
    } finally {
      setLoading(false);
    }
  };

  const updateLocalSettings = (newSettings: Partial<HolidaySettings>) => {
    setHolidaySettings((prev) => ({ ...prev, ...newSettings }));
  };

  const updateDisplaySettings = async (
    newSettings: Partial<DisplaySettings>
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await userSettingsService.updateDisplaySettings(
        newSettings
      );
      // Defensively handle missing displaySettings in response (backwards compatibility)
      // Use spread with defaults to ensure new properties have fallback values
      const displaySettingsDefaults = { showTimezoneIndicator: true, showOverlapIndicator: true, showActiveEntryAnimations: true, showBudgetWarnings: true, overlapIndicatorColor: "#FEF2F2", budgetWarningColor: "#FEF2F2", defaultCurrency: 'EUR' };
      setDisplaySettings({ ...displaySettingsDefaults, ...response.displaySettings });
      setSuccess(response.message || "Display settings updated successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error updating display settings:", err);
      setError("Failed to update display settings. Please try again.");
      throw err; // Re-throw to allow callers to handle rollback
    } finally {
      setLoading(false);
    }
  };

  const updateLocalDisplaySettings = (newSettings: Partial<DisplaySettings>) => {
    setDisplaySettings((prev) => ({ ...prev, ...newSettings }));
  };

  const updateTimezoneSettings = async (
    newSettings: Partial<TimezoneSettings>
  ) => {
    // Guard against duplicate API calls for the same value
    // This can happen when CustomDropdown's grouped options trigger both onClick and onChange
    const newValue = newSettings.preferredTimezone;
    if (pendingTimezoneUpdate.current === newValue) {
      return; // Already processing this exact update
    }
    pendingTimezoneUpdate.current = newValue;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await userSettingsService.updateTimezoneSettings(
        newSettings
      );
      // Merge with defaults for timezone settings
      const timezoneSettingsDefaults = { preferredTimezone: null };
      setTimezoneSettings({ ...timezoneSettingsDefaults, ...response.timezoneSettings });
      setSuccess(response.message || "Timezone settings updated successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error updating timezone settings:", err);
      setError("Failed to update timezone settings. Please try again.");
      throw err; // Re-throw to allow callers to handle rollback
    } finally {
      setLoading(false);
      // Clear the pending update after completion
      pendingTimezoneUpdate.current = undefined;
    }
  };

  const updateLocalTimezoneSettings = (newSettings: Partial<TimezoneSettings>) => {
    setTimezoneSettings((prev) => ({ ...prev, ...newSettings }));
  };

  const updateLocaleSettings = async (
    newSettings: Partial<LocaleSettings>
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await userSettingsService.updateLocaleSettings(
        newSettings
      );
      // Merge with defaults for locale settings
      const localeSettingsDefaults = { weekStartsOn: 'auto' as const };
      setLocaleSettings({ ...localeSettingsDefaults, ...response.localeSettings });
      setSuccess(response.message || "Locale settings updated successfully");

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error("Error updating locale settings:", err);
      setError("Failed to update locale settings. Please try again.");
      throw err; // Re-throw to allow callers to handle rollback
    } finally {
      setLoading(false);
    }
  };

  const updateLocalLocaleSettings = (newSettings: Partial<LocaleSettings>) => {
    setLocaleSettings((prev) => ({ ...prev, ...newSettings }));
  };

  const updateProjectSettings = useCallback(async (newSettings: Partial<ProjectSettings>) => {
    const prev = projectSettings;
    setProjectSettings(s => ({ ...s, ...newSettings })); // optimistic
    try {
      const response = await userSettingsService.updateProjectSettings(newSettings);
      setProjectSettings({ ...{ autoArchiveMonths: 12 }, ...response.projectSettings });
    } catch {
      setProjectSettings(prev); // rollback
      setError('Failed to update project settings');
    }
  }, [projectSettings]);

  const updateLocalProjectSettings = useCallback((newSettings: Partial<ProjectSettings>) => {
    setProjectSettings(prev => ({ ...prev, ...newSettings }));
  }, []);

  // Computed value: effective week start day (0 = Sunday, 1 = Monday)
  // Uses user setting if explicitly set, otherwise auto-detects from locale
  const effectiveWeekStartDay: WeekStartDay = getWeekStartDay(localeSettings.weekStartsOn);

  return {
    holidaySettings,
    displaySettings,
    timezoneSettings,
    localeSettings,
    projectSettings,
    effectiveWeekStartDay,
    countries,
    states,
    loading,
    error,
    success,
    updateHolidaySettings,
    updateDisplaySettings,
    updateTimezoneSettings,
    updateLocaleSettings,
    updateProjectSettings,
    updateLocalSettings,
    updateLocalDisplaySettings,
    updateLocalTimezoneSettings,
    updateLocalLocaleSettings,
    updateLocalProjectSettings,
    setError,
    setSuccess,
  };
}
